import React, { useState, useEffect } from 'react';
import { createMatch } from '../../services/leagueService';
import {
  generatePlayoffBracket,
  getPlayoffFormatOptions,
  validatePlayoffReadiness
} from '../../utils/scheduleGenerator';

const GeneratePlayoffsModal = ({ leagueId, standings, matches, onClose, onSuccess }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState(null);
  const [options, setOptions] = useState({
    playoffCount: 4,
    location: ''
  });

  useEffect(() => {
    const validationResult = validatePlayoffReadiness(standings, matches);
    setValidation(validationResult);

    if (!validationResult.isReady) {
      setError(validationResult.message);
    }
  }, [standings, matches]);

  const playoffFormatOptions = validation?.isReady
    ? getPlayoffFormatOptions(validation.participantCount)
    : [];

  // Set default playoff count to largest valid option
  useEffect(() => {
    if (playoffFormatOptions.length > 0 && !options.playoffCount) {
      const defaultOption = playoffFormatOptions[playoffFormatOptions.length - 1];
      setOptions(prev => ({ ...prev, playoffCount: defaultOption.value }));
    }
  }, [playoffFormatOptions]);

  const selectedFormat = playoffFormatOptions.find(opt => opt.value === options.playoffCount);
  const totalPlayoffMatches = options.playoffCount ? options.playoffCount - 1 : 0;

  const handleGenerate = async () => {
    if (!validation?.isReady) {
      setError('Cannot generate playoffs. Check requirements above.');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      // Sort standings by rank (or calculate if needed)
      const sortedStandings = [...standings].sort((a, b) => {
        // Primary: Win percentage
        const aWinPct = a.matchesPlayed > 0 ? a.wins / a.matchesPlayed : 0;
        const bWinPct = b.matchesPlayed > 0 ? b.wins / b.matchesPlayed : 0;
        if (bWinPct !== aWinPct) return bWinPct - aWinPct;

        // Secondary: Total wins
        if (b.wins !== a.wins) return b.wins - a.wins;

        // Tertiary: Points differential
        const aDiff = (a.pointsFor || 0) - (a.pointsAgainst || 0);
        const bDiff = (b.pointsFor || 0) - (b.pointsAgainst || 0);
        return bDiff - aDiff;
      });

      const playoffMatches = generatePlayoffBracket(sortedStandings, options.playoffCount);

      // Add location if provided
      const matchesWithLocation = options.location
        ? playoffMatches.map(match => ({ ...match, location: options.location }))
        : playoffMatches;

      // Determine the starting round number (after regular season)
      const maxRegularSeasonRound = Math.max(
        ...matches.filter(m => !m.isPlayoff).map(m => m.round || 1),
        0
      );

      // Create playoff matches with correct round numbers
      const createPromises = matchesWithLocation.map(match =>
        createMatch(leagueId, {
          ...match,
          round: maxRegularSeasonRound + match.round
        })
      );

      await Promise.all(createPromises);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error generating playoffs:', err);
      setError(err.message || 'Failed to generate playoffs. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="ls-generate-playoffs-modal">
      <div className="ls-modal-content">
        <div className="ls-modal-header">
          <h2>Generate Playoffs</h2>
          <button onClick={onClose} className="ls-close-btn" aria-label="Close modal">
            <IconCircleX size={24} />
          </button>
        </div>

        <div className="ls-modal-body">
          {!validation?.isReady ? (
            <div className="ls-validation-error">
              <IconAlertCircle size={24} color="#c41e3a" />
              <div className="ls-error-content">
                <h3>Not Ready for Playoffs</h3>
                <p>{validation?.message || 'Checking playoff readiness...'}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="ls-playoffs-info">
                <IconTrophy size={24} color="#ffd700" />
                <div className="ls-info-text">
                  <p className="ls-info-title">Playoff Bracket Generation</p>
                  <p className="ls-info-description">
                    Generate single-elimination playoff bracket from current standings
                  </p>
                </div>
              </div>

              <div className="ls-standings-preview">
                <h3>Top Performers</h3>
                <div className="ls-top-performers-list">
                  {standings.slice(0, Math.min(8, standings.length)).map((participant, index) => {
                    const willMakePlayoffs = index < options.playoffCount;
                    return (
                      <div
                        key={participant.participantId}
                        className={`ls-performer-item ${willMakePlayoffs ? 'ls-in-playoffs' : 'ls-out'}`}
                      >
                        <div className="ls-performer-rank">#{index + 1}</div>
                        <div className="ls-performer-name">{participant.participantName}</div>
                        <div className="ls-performer-record">
                          {participant.wins}-{participant.losses}
                          {participant.winPercentage !== undefined && (
                            <span className="ls-win-pct">
                              ({(participant.winPercentage * 100).toFixed(0)}%)
                            </span>
                          )}
                        </div>
                        {willMakePlayoffs && <div className="ls-playoff-badge">IN</div>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="ls-form-group">
                <label htmlFor="playoffCount">Playoff Format</label>
                <select
                  id="playoffCount"
                  value={options.playoffCount}
                  onChange={(e) => setOptions({ ...options, playoffCount: Number(e.target.value) })}
                >
                  {playoffFormatOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {selectedFormat && (
                  <p className="ls-form-hint">{selectedFormat.description}</p>
                )}
              </div>

              <div className="ls-playoff-stats">
                <div className="ls-stat-item">
                  <span className="ls-stat-label">Playoff Teams</span>
                  <span className="ls-stat-value">{options.playoffCount}</span>
                </div>
                <div className="ls-stat-item">
                  <span className="ls-stat-label">Playoff Matches</span>
                  <span className="ls-stat-value">{totalPlayoffMatches}</span>
                </div>
              </div>

              <div className="ls-form-group">
                <label htmlFor="location">Playoff Location (Optional)</label>
                <input
                  type="text"
                  id="location"
                  value={options.location}
                  onChange={(e) => setOptions({ ...options, location: e.target.value })}
                  placeholder="e.g., Championship Court"
                />
                <p className="ls-form-hint">Default location for all playoff matches</p>
              </div>

              <div className="ls-seeding-note">
                <IconInfoCircle size={18} color="#00d4ff" />
                <span>
                  Teams are seeded by: Win percentage → Total wins → Point differential
                </span>
              </div>
            </>
          )}

          {error && validation?.isReady && (
            <div className="ls-error-message">{error}</div>
          )}
        </div>

        <div className="ls-modal-actions">
          <button
            type="button"
            onClick={onClose}
            className="ls-btn-secondary"
            disabled={generating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            className="ls-btn-primary"
            disabled={generating || !validation?.isReady}
          >
            {generating ? 'Generating...' : `Generate Playoffs`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneratePlayoffsModal;
